import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { Api } from 'lib';

/*
  Loads the preset shout-out messages for a team and resolves their templates.

  Shared by every flow that can shout out a team (the CTA share-link screen and
  the in-app shout-out modal) so the pills, the cooldown fallback and the
  {teamName} substitution behave identically in all of them.
*/
export default function useShoutoutPresets({ teamId, teamName, active = true }) {
  const { language } = useSelector((state) => state.appState);

  const [presets, setPresets] = useState([]);
  const [fallback, setFallback] = useState(null);
  // starts true: the pills are the first thing to look at, so show the loader
  // rather than an empty gap until the request lands
  const [loading, setLoading] = useState(true);

  /*
    Refetches every time the surface becomes active again, not just on mount:
    the shout-out modal is hidden rather than unmounted, and eligibility changes
    server-side as soon as a preset is used — a cached list would re-offer a
    preset the backend has already retired for this user.
  */
  useEffect(() => {
    if (teamId && active) fetchPresets();
  }, [teamId, active]);

  async function fetchPresets() {
    setLoading(true);
    try {
      // teamId lets the backend pin the team's own preset (if set) into slot 1
      const res = await Api.get(`/shoutouts/getPresets?teamId=${teamId}`);
      if (res?.presets) {
        setPresets(res.presets);
        // set when fewer than 3 presets are eligible (per-user cooldowns) —
        // presets is empty and checkBackAt says when a 3rd frees up
        setFallback(res.fallback || null);
      }
    } finally {
      setLoading(false);
    }
  }

  /*
    Preset texts are stored as templates — the backend can't know which team
    the flow points at, so it ships the literal {teamName} token and the
    client fills it in from the target it already has.
  */
  function getPresetText(preset) {
    if (!preset) return '';

    const raw = (language === 'ja' && preset.ja) || preset.en;
    return raw.replace(/\{teamName\}/gi, teamName || '');
  }

  return { presets, fallback, loading, refetch: fetchPresets, getPresetText };
}
